// Alert — info / success / warning / danger banner.
// role="alert" for danger (announced immediately by screen readers);
// role="status" for info/success/warning (announced politely).

import type { HTMLAttributes, ReactNode } from "react";

import { tokens } from "../tokens/tokens";

type Intent = "info" | "success" | "warning" | "danger";

export interface AlertProps extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
  intent?: Intent;
  /** Heading text — bold, first line. Optional. */
  title?: string;
  children: ReactNode;
}

export function Alert({ intent = "info", title, children, ...rest }: AlertProps) {
  const palette = palettes[intent];
  return (
    <div
      {...rest}
      role={intent === "danger" ? "alert" : "status"}
      style={{
        background: palette.bg,
        border: `1px solid ${palette.border}`,
        borderLeft: `4px solid ${palette.accent}`,
        color: palette.fg,
        borderRadius: tokens.radius.md,
        padding: tokens.space[3],
        fontSize: tokens.type.size.sm,
        ...rest.style,
      }}
    >
      {title && (
        <p
          style={{
            fontWeight: tokens.type.weight.semibold,
            marginBottom: tokens.space[1],
          }}
        >
          {title}
        </p>
      )}
      {children}
    </div>
  );
}

const palettes: Record<
  Intent,
  { bg: string; border: string; accent: string; fg: string }
> = {
  info: {
    bg: tokens.color.info[50],
    border: "#bfdbfe",
    accent: tokens.color.info[500],
    fg: tokens.color.info[700],
  },
  success: {
    bg: tokens.color.success[50],
    border: "#bbf7d0",
    accent: tokens.color.success[500],
    fg: tokens.color.success[700],
  },
  warning: {
    bg: tokens.color.warning[50],
    border: "#fde68a",
    accent: tokens.color.warning[500],
    fg: tokens.color.warning[700],
  },
  danger: {
    bg: tokens.color.danger[50],
    border: "#fecaca",
    accent: tokens.color.danger[500],
    fg: tokens.color.danger[700],
  },
};
